// SPDX-License-Identifier: LicenseRef-Proprietary

import { useState, useEffect, useCallback } from 'react';
import type { ResultFilter } from '../types/resultCategorization';

/**
 * Hook for managing category state persistence across navigation
 */
export interface UseCategoryStateResult {
  expandedCategories: Set<string>;
  activeFilter: ResultFilter;
  toggleCategory: (category: string) => void;
  setFilter: (filter: ResultFilter) => void;
  resetState: () => void;
}

/**
 * Storage keys for persisting category state
 */
const STORAGE_KEYS = {
  EXPANDED_CATEGORIES: 'evidence-drawer-expanded-categories',
  ACTIVE_FILTER: 'evidence-drawer-active-filter'
} as const;

/**
 * Default expanded categories (highly relevant and somewhat relevant)
 */
const DEFAULT_EXPANDED_CATEGORIES = new Set(['highlyRelevant', 'somewhatRelevant']);

/**
 * Default filter (show all results)
 */
const DEFAULT_FILTER: ResultFilter = 'all';

/**
 * Custom hook for managing categorized results state with persistence
 */
export function useCategoryState(): UseCategoryStateResult {
  // Initialize state from localStorage or defaults
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.EXPANDED_CATEGORIES);
      if (stored) {
        const parsed = JSON.parse(stored);
        return new Set(Array.isArray(parsed) ? parsed : []);
      }
    } catch (error) {
      console.warn('Failed to parse stored expanded categories:', error);
    }
    return DEFAULT_EXPANDED_CATEGORIES;
  });

  const [activeFilter, setActiveFilter] = useState<ResultFilter>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.ACTIVE_FILTER);
      if (stored && ['all', 'relevant', 'not_relevant', 'failed', 'not_analyzed'].includes(stored)) {
        return stored as ResultFilter;
      }
    } catch (error) {
      console.warn('Failed to parse stored active filter:', error);
    }
    return DEFAULT_FILTER;
  });

  // Persist expanded categories to localStorage
  useEffect(() => {
    try {
      const categoriesArray = Array.from(expandedCategories);
      localStorage.setItem(STORAGE_KEYS.EXPANDED_CATEGORIES, JSON.stringify(categoriesArray));
    } catch (error) {
      console.warn('Failed to persist expanded categories:', error);
    }
  }, [expandedCategories]);

  // Persist active filter to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.ACTIVE_FILTER, activeFilter);
    } catch (error) {
      console.warn('Failed to persist active filter:', error);
    }
  }, [activeFilter]);

  // Toggle category expansion state
  const toggleCategory = useCallback((category: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  }, []);

  // Set active filter
  const setFilter = useCallback((filter: ResultFilter) => {
    setActiveFilter(filter);
  }, []);

  // Reset state to defaults
  const resetState = useCallback(() => {
    setExpandedCategories(DEFAULT_EXPANDED_CATEGORIES);
    setActiveFilter(DEFAULT_FILTER);
    
    // Clear localStorage
    try {
      localStorage.removeItem(STORAGE_KEYS.EXPANDED_CATEGORIES);
      localStorage.removeItem(STORAGE_KEYS.ACTIVE_FILTER);
    } catch (error) {
      console.warn('Failed to clear category state from localStorage:', error);
    }
  }, []);

  return {
    expandedCategories,
    activeFilter,
    toggleCategory,
    setFilter,
    resetState
  };
}

/**
 * Utility function to check if localStorage is available
 */
export function isLocalStorageAvailable(): boolean {
  try {
    const test = '__localStorage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
}